import { Destination, ManualEntry, PriceRow, Product, Quarry, Quote } from '../types/catalog'
import { prices } from '../data/prices'
import { products } from '../data/products'
import { quarries } from '../data/quarries'
import { roadDistanceMiles } from './geo'

export type SortKey = 'price' | 'distance'

export interface QuoteResult {
  quotes: Quote[]
  /** Plants carrying the product but outside the haul radius. */
  outsideRadius: number
}

export interface BuildQuotesOptions {
  manualEntries: ManualEntry[]
  includeOutsideRadius: boolean
  haulRatePerMile: number
}

export function searchProducts(query: string, limit = 7): Product[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  return products
    .filter((product) => {
      const haystack = [product.name, product.category, product.productNumber, ...product.aliases]
        .join(' ')
        .toLowerCase()
      return haystack.includes(q)
    })
    .slice(0, limit)
}

export function buildQuotes(
  product: Product,
  destination: Destination,
  radiusMiles: number,
  sortBy: SortKey,
  { manualEntries, includeOutsideRadius, haulRatePerMile }: BuildQuotesOptions,
): QuoteResult {
  const listed: Quote[] = prices
    .filter((row) => row.productId === product.id)
    .map((row) => {
      const quarry = quarries.find((q) => q.id === row.quarryId)!
      return toQuote(quarry, { ...row, source: 'price-list' }, destination, haulRatePerMile)
    })

  const manual: Quote[] = manualEntries
    .filter((entry) => entry.productId === product.id)
    .map((entry) => {
      const row: PriceRow = {
        productId: entry.productId,
        quarryId: entry.quarry.id,
        price: entry.price,
        minLoadCharge: entry.minLoadCharge,
        source: 'manual',
        note: entry.note,
        entryId: entry.id,
      }
      return toQuote(entry.quarry, row, destination, haulRatePerMile)
    })

  const all = [...listed, ...manual]
  const withinRadius = all.filter((quote) => quote.distanceMiles <= radiusMiles)
  const visible = includeOutsideRadius ? all : withinRadius

  // Rows quoted in the product's primary unit rank first; a per-cubic-yard
  // delivered price can't be compared against a per-ton price at the scale.
  const unitRank = (quote: Quote) => (priceUnit(quote.price, product) === product.unit ? 0 : 1)
  const quotes = [...visible].sort(
    (a, b) =>
      unitRank(a) - unitRank(b) ||
      (sortBy === 'distance' ? a.distanceMiles - b.distanceMiles : a.price.price - b.price.price),
  )

  return { quotes, outsideRadius: all.length - withinRadius.length }
}

/** The unit a single price row is quoted in. */
export function priceUnit(row: PriceRow, product: Product): string {
  return row.unit ?? product.unit
}

export interface LoadTotal {
  /** Units actually charged, e.g. 15 tons. */
  quantity: number
  /** Unit those units are in — the row's unit, not always the product's. */
  unit: string
  /** Material at the listed price, before any adders. */
  material: number
  /** Seller's per-unit surcharge stack across the whole load. */
  surcharge: number
  /** Per-load delivery fee, where the seller's price is delivered. */
  delivery: number
  /** True when the load minimum is higher than the material total. */
  minimumApplied: boolean
  total: number
}

/**
 * What one load actually costs at this plant: listed price plus the seller's
 * per-unit surcharges, floored at their minimum charge, plus any delivery fee.
 * Products priced by the load, gallon or each are charged as a single unit.
 */
export function loadTotal(quote: Quote, product: Product, loadUnits: number): LoadTotal {
  const unit = priceUnit(quote.price, product)
  const bulkUnit = unit === 'ton' || unit === 'cu yd'
  const quantity = bulkUnit ? loadUnits : 1
  const material = quote.price.price * quantity
  const surcharge = quote.quarry.surchargePerUnit * quantity
  const delivery = quote.price.deliveryFee ?? 0
  const floored = Math.max(material + surcharge, quote.price.minLoadCharge)
  return {
    quantity,
    unit,
    material,
    surcharge,
    delivery,
    minimumApplied: quote.price.minLoadCharge > material + surcharge,
    total: floored + delivery,
  }
}

function toQuote(
  quarry: Quarry,
  price: PriceRow,
  destination: Destination,
  haulRatePerMile: number,
): Quote {
  const distanceMiles = roadDistanceMiles(quarry.coords, destination.coords)
  return { quarry, price, distanceMiles, haulCost: distanceMiles * haulRatePerMile }
}
